import React, { useState, useEffect, memo } from 'react';
import {
  Button,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  message,
  Select,
  Table,
  Space,
} from 'antd';
import dayjs from 'dayjs';
import ImageCropper from '@/components/ImageCropper';
import {
  createNewcomerChallenge,
  updateNewcomerChallenge,
  getVipRights,
  getActivityRuleOptions,
  getAwardTypes,
  type VipRight,
  type ActivityRuleOption,
  type AwardType,
} from './api';
import type { NewcomerChallenge, RewardSetting } from './types';

const { RangePicker } = DatePicker;
const { TextArea } = Input;

// 权重设置组件
const WeightSettings = memo<{ disabled?: boolean }>(({ disabled = false }) => {
  const calculateTotalWeight = (allValues: any) => {
    const keys = [
      'voteWeight',
      'likeWeight',
      'commentWeight',
      'collectWeight',
    ];
    return keys.reduce((acc, key) => acc + (Number(allValues[key]) || 0), 0);
  };

  return (
    <Form.Item
      noStyle
      shouldUpdate={(prev, cur) => {
        const keys = [
          'voteWeight',
          'likeWeight',
          'commentWeight',
          'collectWeight',
        ];
        return keys.some((k) => prev[k] !== cur[k]);
      }}
    >
      {({ getFieldsValue }) => {
        const total = calculateTotalWeight(getFieldsValue());
        return (
          <>
            <div style={{ marginBottom: 16 }}>
              <h3>权重设置</h3>
              <p style={{ color: total === 100 ? 'green' : 'red' }}>
                当前权重总和: {total}% {total === 100 ? '✓' : '(必须等于100%)'}
              </p>
            </div>
            {[
              'voteWeight',
              'likeWeight',
              'commentWeight',
              'collectWeight',
            ].map((field, idx) => (
              <Form.Item
                key={field}
                name={field}
                label={['投票', '点赞', '评论', '收藏'][idx] + '权重'}
                rules={[
                  { required: true, message: `请输入${['投票', '点赞', '评论', '收藏'][idx]}权重` },
                  { type: 'number', min: 0, max: 100, message: '范围0-100' },
                ]}
              >
                <InputNumber min={0} max={100} style={{ width: '100%' }} disabled={disabled} />
              </Form.Item>
            ))}
          </>
        );
      }}
    </Form.Item>
  );
});

WeightSettings.displayName = 'WeightSettings';

interface NewcomerChallengeDrawerProps {
  visible: boolean;
  editData?: NewcomerChallenge;
  onSuccess: () => void;
  onClose: () => void;
}

const NewcomerChallengeDrawer: React.FC<NewcomerChallengeDrawerProps> = ({
  visible,
  editData,
  onSuccess,
  onClose,
}) => {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  // 判断是否为查看模式：只有状态为未开始（0）时才允许编辑(需求暂时隐藏)
  // const isViewMode = editData ? editData.status !== 0 : false;
  const isViewMode = false;
  const [vipRightsList, setVipRightsList] = useState<VipRight[]>([]);
  const [loadingVipRights, setLoadingVipRights] = useState(false);
  const [activityRuleOptions, setActivityRuleOptions] = useState<ActivityRuleOption[]>([]);
  const [loadingActivityRules, setLoadingActivityRules] = useState(false);
  const [awardTypes, setAwardTypes] = useState<AwardType[]>([]);
  const [loadingAwardTypes, setLoadingAwardTypes] = useState(false);

  const picList = [
    // 第一行：日语版本
    {
      id: 'rookieKingCoverJa',
      name: 'Rookie King封面图(日语)',
      // 输出尺寸放大 3× 保留比例（347:513 = 1041:1539），客户端展示更清晰
      width: 1041,
      height: 1539,
      isRequired: true,
      noCrop: false,
    },
    {
      id: 'previousCoverJa',
      name: 'previous封面图(日语)',
      // 输出尺寸放大 5× 保留比例（128:70 = 640:350），客户端展示更清晰
      width: 640,
      height: 350,
      isRequired: true,
      noCrop: false,
    },
    {
      id: 'previousListJa',
      name: 'previous列表图(日语)',
      // 输出尺寸放大 3× 保留比例（347:175 = 1041:525），客户端展示更清晰
      width: 1041,
      height: 525,
      isRequired: true,
      noCrop: false,
    },
    {
      id: 'detailBgJa',
      name: '详情页背景图(日语)',
      // 输出尺寸放大 3× 保留比例（375:384 = 1125:1152），客户端展示更清晰
      width: 1125,
      height: 1152,
      isRequired: true,
      noCrop: false,
    },
    // 第二行：英语版本
    {
      id: 'rookieKingCover',
      name: 'Rookie King封面图(英语)',
      // 输出尺寸放大 3× 保留比例（347:513 = 1041:1539），客户端展示更清晰
      width: 1041,
      height: 1539,
      isRequired: true,
      noCrop: false,
    },
    {
      id: 'previousCover',
      name: 'previous封面图(英语)',
      // 输出尺寸放大 5× 保留比例（128:70 = 640:350），客户端展示更清晰
      width: 640,
      height: 350,
      isRequired: true,
      noCrop: false,
    },
    {
      id: 'previousList',
      name: 'previous列表图(英语)',
      // 输出尺寸放大 3× 保留比例（347:175 = 1041:525），客户端展示更清晰
      width: 1041,
      height: 525,
      isRequired: true,
      noCrop: false,
    },
    {
      id: 'detailBg',
      name: '详情页背景图(英语)',
      // 输出尺寸放大 3× 保留比例（375:384 = 1125:1152），客户端展示更清晰
      width: 1125,
      height: 1152,
      isRequired: true,
      noCrop: false,
    },
  ];

  // 加载VIP权益列表
  useEffect(() => {
    if (visible) {
      setLoadingVipRights(true);
      getVipRights()
        .then((res) => {
          if (res.status === 200 && res.data) {
            // 过滤 isEnabled === 1 的数据
            const enabledRights = res.data.filter((item) => item.isEnabled === 1);
            setVipRightsList(enabledRights);
          }
        })
        .catch(() => {
          messageApi.error('获取VIP权益列表失败');
        })
        .finally(() => {
          setLoadingVipRights(false);
        });
    } else {
      setVipRightsList([]);
    }
  }, [visible, messageApi]);

  // 加载活动规则列表（用于规则说明下拉框）
  useEffect(() => {
    if (visible) {
      setLoadingActivityRules(true);
      getActivityRuleOptions()
        .then((res) => {
          if (res.status === 200 && res.data) {
            setActivityRuleOptions(res.data);
          }
        })
        .catch(() => {
          messageApi.error('获取活动规则列表失败');
        })
        .finally(() => {
          setLoadingActivityRules(false);
        });
    } else {
      setActivityRuleOptions([]);
    }
  }, [visible, messageApi]);

  // 加载奖励类型列表
  useEffect(() => {
    if (visible) {
      setLoadingAwardTypes(true);
      getAwardTypes()
        .then((res) => {
          if ((res.status === 200 || res.success) && res.data && Array.isArray(res.data)) {
            setAwardTypes(res.data);
            // 新增时，根据接口数据自动初始化所有奖励项
            if (!editData && res.data.length > 0) {
              const initialRewards = res.data.map((item) => ({
                type: item.id,
                num: 0,
                purpleCoins: 0,
                cash: 0,
                dayType: item.dayType,
              }));
              form.setFieldsValue({ rewards: initialRewards });
            }
          }
        })
        .catch(() => {
          messageApi.error('获取奖励类型列表失败');
        })
        .finally(() => {
          setLoadingAwardTypes(false);
        });
    } else {
      setAwardTypes([]);
    }
  }, [visible, messageApi, editData, form]);

  useEffect(() => {
    if (visible) {
      const initWeight = {
        voteWeight: 0,
        likeWeight: 100,
        commentWeight: 0,
        collectWeight: 0,
      };
      const initial = editData
        ? {
            ...editData,
            submitTime:
              editData.submitBeginTime && editData.submitEndTime
                ? [
                    dayjs(Number(editData.submitBeginTime)),
                    dayjs(Number(editData.submitEndTime)),
                  ]
                : undefined,
            voteTime:
              editData.voteBeginTime && editData.voteEndTime
                ? [
                    dayjs(Number(editData.voteBeginTime)),
                    dayjs(Number(editData.voteEndTime)),
                  ]
                : undefined,
            rewards: editData.rewardSubList && editData.rewardSubList.length > 0
              ? editData.rewardSubList.map((reward) => ({
                  type: reward.type,
                  num: reward.winnerCount || 0,
                  purpleCoins: reward.purpleCoin || 0,
                  cash: reward.cashAmount || 0,
                  vipBenefits: reward.vipRightsId,
                  dayType: reward.dayType,
                }))
              : editData.rewards && editData.rewards.length > 0
              ? editData.rewards.map((reward) => ({
                  type: reward.type,
                  num: reward.num || 0,
                  purpleCoins: reward.purpleCoins || 0,
                  cash: reward.cash || 0,
                  vipBenefits: reward.vipBenefits,
                  dayType: reward.dayType,
                }))
              : [],
            voteWeight: Number(editData.voteWeight) ?? initWeight.voteWeight,
            likeWeight: Number(editData.likeWeight) ?? initWeight.likeWeight,
            commentWeight: Number(editData.commentWeight) ?? initWeight.commentWeight,
            collectWeight: Number(editData.collectWeight) ?? initWeight.collectWeight,
          }
        : {
            rewards: [],
            voteWeight: initWeight.voteWeight,
            likeWeight: initWeight.likeWeight,
            commentWeight: initWeight.commentWeight,
            collectWeight: initWeight.collectWeight,
          };
      form.setFieldsValue(initial);
    } else {
      form.resetFields();
    }
  }, [visible, editData, form]);

  const checkWeightSum = (values: any) => {
    const keys = [
      'voteWeight',
      'likeWeight',
      'commentWeight',
      'collectWeight',
    ];
    return (
      keys.reduce((sum, key) => sum + (Number(values[key]) || 0), 0) === 100
    );
  };

  const onSubmit = async (values: any) => {
    // 验证权重总和
    if (!checkWeightSum(values)) {
      messageApi.error('权重总和必须等于100%');
      return;
    }

    const [submitBeginTime, submitEndTime] = values.submitTime;
    const [voteBeginTime, voteEndTime] = values.voteTime;

    // 处理奖励数据，过滤掉没有type的项
    const rewards = (values.rewards || []).filter((reward: any) => reward.type !== undefined);

    const params: NewcomerChallenge = {
      ...values,
      rewards,
      submitBeginTime: submitBeginTime.valueOf(),
      submitEndTime: submitEndTime.valueOf(),
      voteBeginTime: voteBeginTime.valueOf(),
      voteEndTime: voteEndTime.valueOf(),
      id: editData?.id,
    };
    delete params.submitTime;
    delete params.voteTime;

    const resp = editData
      ? await updateNewcomerChallenge(params)
      : await createNewcomerChallenge(params);

    if (resp.status === 200) {
      messageApi.success(`${editData ? '更新' : '创建'}成功`);
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 500);
    } else {
      messageApi.error(resp.message || `${editData ? '更新' : '创建'}失败`);
    }
  };

  const rewardColumns = [
    {
      title: '名次',
      key: 'type',
      width: 150,
      render: (_: any, record: any) => (
        <Form.Item noStyle shouldUpdate={(prev, cur) => {
          const prevType = prev.rewards?.[record.name]?.type;
          const curType = cur.rewards?.[record.name]?.type;
          return prevType !== curType;
        }}>
          {() => {
            const typeValue = form.getFieldValue(['rewards', record.name, 'type']);
            const awardType = awardTypes.find((item) => item.id === typeValue);
            return awardType ? awardType.typeName : '-';
          }}
        </Form.Item>
      ),
    },
    // {
    //   title: '奖励类型',
    //   key: 'type',
    //   width: 150,
    //   render: (_: any, record: any) => (
    //     <Form.Item
    //       name={[record.name, 'type']}
    //       rules={[{ required: true, message: '请选择奖励类型' }]}
    //       style={{ margin: 0 }}
    //     >
    //       <Select 
    //         placeholder="请选择奖励类型" 
    //         allowClear
    //         loading={loadingAwardTypes}
    //         options={awardTypes.map((item) => ({
    //           label: item.typeName || `类型${item.id}`,
    //           value: item.id,
    //         }))}
    //         disabled
    //       />
    //     </Form.Item>
    //   ),
    // },
    {
      title: '人数',
      key: 'num',
      width: 120,
      render: (_: any, record: any, index: number) => (
        <Form.Item
          name={[record.name, 'num']}
          rules={[{ required: true, message: '请输入人数' }]}
          style={{ margin: 0 }}
        >
          <InputNumber min={0} style={{ width: '100%' }} disabled={isViewMode} />
        </Form.Item>
      ),
    },
    {
      title: '活动金币(枚)',
      key: 'purpleCoins',
      width: 120,
      render: (_: any, record: any, index: number) => (
        <Form.Item
          name={[record.name, 'purpleCoins']}
          rules={[{ required: true, message: '请输入活动金币' }]}
          style={{ margin: 0 }}
        >
          <InputNumber min={0} style={{ width: '100%' }} disabled={isViewMode} />
        </Form.Item>
      ),
    },
    {
      title: '现金',
      key: 'cash',
      width: 120,
      render: (_: any, record: any, index: number) => (
        <Form.Item
          name={[record.name, 'cash']}
          rules={[{ required: true, message: '请输入现金' }]}
          style={{ margin: 0 }}
        >
          <InputNumber min={0} style={{ width: '100%' }} disabled={isViewMode} />
        </Form.Item>
      ),
    },
    {
      title: 'VIP权益',
      key: 'vipBenefits',
      width: 150,
      render: (_: any, record: any, index: number) => (
        <Form.Item
          name={[record.name, 'vipBenefits']}
          style={{ margin: 0 }}
        >
          <Select 
            placeholder="请选择VIP权益" 
            allowClear
            loading={loadingVipRights}
            disabled={isViewMode}
            options={vipRightsList.map((item) => ({
              label: item.rightsName,
              value: item.id,
            }))}
          />
        </Form.Item>
      ),
    },
  ];

  return (
    <Drawer
      open={visible}
      width={1000}
      onClose={onClose}
      destroyOnClose
      maskClosable={false}
      title={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>
            {isViewMode ? '查看' : editData ? '编辑' : '创建'}新人王挑战赛
          </span>
          <div>
            <Button onClick={onClose}>
              {isViewMode ? '关闭' : '取消'}
            </Button>
            {!isViewMode && (
              <Button
                type="primary"
                onClick={async () => {
                  try {
                    await form.validateFields();
                    form.submit();
                  } catch (error) {
                    messageApi.error('请填写所有必填项');
                  }
                }}
                style={{ marginLeft: 8 }}
              >
                确定
              </Button>
            )}
          </div>
        </div>
      }
    >
      {contextHolder}
      <Form
        layout="vertical"
        form={form}
        onFinish={onSubmit}
        style={{ width: 760 }}
      >
        <Form.Item
          name="title"
          label="主标题"
          rules={[{ required: true, message: '请输入主标题' }]}
        >
          <Input placeholder="请输入主标题" disabled={isViewMode} />
        </Form.Item>
        <Form.Item
          name="subtitle"
          label="副标题"
          rules={[{ required: true, message: '请输入副标题' }]}
        >
          <Input placeholder="请输入副标题" disabled={isViewMode} />
        </Form.Item>

        <Form.Item
          label="活动图片"
          required
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
            {picList.map((value) => (
              <div key={value.id}>
                <Form.Item
                  name={value.id}
                  rules={
                    value.isRequired
                      ? [{ required: true, message: `请上传${value.name}` }]
                      : []
                  }
                >
                  <ImageCropper
                    aspectRatio={value.width / value.height}
                    width={value.width}
                    height={value.height}
                    quality={1}
                    fixedCropBox={true}
                    cropBoxWidth={value.width}
                    cropBoxHeight={value.height}
                    autoUpload={true}
                    showCroppedPreview={true}
                    picDesc={value.name}
                    fileTypes={['image/png', 'image/jpeg', 'image/jpg', 'image/webp']}
                    maxFileSizeMB={1}
                    enablePreview={true}
                    noCrop={value.noCrop}
                    disabled={isViewMode}
                    enableModeSwitch={true}
                    defaultUploadMode="crop"
                    onUploadSuccess={(url) => {
                      console.log('上传成功，图片URL:', url);
                    }}
                    onUploadError={(error) => {
                      console.error('上传失败:', error);
                      message.error('上传失败');
                    }}
                  />
                </Form.Item>
                <div style={{ textAlign: 'center', fontSize: 12, marginTop: 4 }}>
                  <div>{value.name}</div>
                  <div style={{ color: '#999' }}>
                    分辨率：{value.width}x{value.height}px
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Form.Item>

        <Form.Item
          name="submitTime"
          label="投稿时间（日本时间）"
          rules={[{ required: true, message: '请选择投稿时间范围' }]}
        >
          <RangePicker
            showTime
            format="YYYY-MM-DD HH:mm"
            placeholder={['开始时间', '结束时间']}
            disabled={isViewMode}
          />
        </Form.Item>
        <Form.Item
          name="voteTime"
          label="用户评审时间（日本时间）"
          dependencies={['submitTime']}
          rules={[
            { required: true, message: '请选择用户评审时间范围' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || !Array.isArray(value) || value.length < 2) {
                  return Promise.resolve();
                }
                const submitTime = getFieldValue('submitTime');
                if (!submitTime || !Array.isArray(submitTime) || submitTime.length < 2) {
                  return Promise.resolve();
                }
                const voteBeginTime = value[0];
                const submitBeginTime = submitTime[0];
                if (voteBeginTime && submitBeginTime && voteBeginTime.isBefore(submitBeginTime)) {
                  return Promise.reject(new Error('Voting time must be later than the submission time.'));
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <RangePicker
            showTime
            format="YYYY-MM-DD HH:mm"
            placeholder={['开始时间', '结束时间']}
            disabled={isViewMode}
          />
        </Form.Item>

        {/* <Form.Item
          name="eventDescription"
          label="活动简介"
          rules={[{ required: true, message: '请输入活动简介' }]}
        >
          <Input placeholder="请输入活动简介" />
        </Form.Item> */}

        <Form.Item label="奖励设置">
          <Form.List name="rewards">
            {(fields) => (
              <Table
                dataSource={fields}
                columns={rewardColumns}
                rowKey={(record) => record.key}
                pagination={false}
                bordered
              />
            )}
          </Form.List>
        </Form.Item>

        <Form.Item name="otherRewardDesc" label="其他奖励描述">
          <TextArea
            placeholder="请输入其他奖励描述"
            rows={4}
            disabled={isViewMode}
          />
        </Form.Item>

        <Form.Item
          name="ruleId"
          label="规则说明"
          rules={[{ required: true, message: '请选择规则说明' }]}
        >
          <Select 
            placeholder="从规则库中选择" 
            allowClear
            loading={loadingActivityRules}
            disabled={isViewMode}
            options={activityRuleOptions.map((item) => {
              const en = item.activityTopicEn || '';
              const ja = item.activityTopicJa || '';
              const label = en && ja ? `${en} / ${ja}` : (en || ja || '未命名规则');
              return {
                label,
                value: item.id,
              };
            })}
          />
        </Form.Item>

        <WeightSettings disabled={isViewMode} />
      </Form>
    </Drawer>
  );
};

export default NewcomerChallengeDrawer;

